import { Link } from 'react-router-dom';
import { TRANSPARENCY_CRITERIA, TRANSPARENCY_TIERS } from '@affily/shared/transparency';
import { CLICK_DEDUP_WINDOW_MS } from '@affily/shared/constants';
import { Seo } from '../components/Seo.jsx';

/**
 * The rubric page.
 *
 * The transparency score would be self-undermining if the rules behind it were
 * not themselves public, so this page publishes the criteria list directly from
 * the same module that computes the score. It cannot drift from the real rubric
 * because it IS the real rubric.
 */
export default function Transparency() {
  return (
    <>
      <Seo
        title="How transparency works on Affily"
        description="The rules behind the transparency score, what counts as verified versus self-reported, and who sees a program's terms."
      />

      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h1 className="text-4xl font-semibold tracking-tight">How transparency works</h1>
        <p className="mt-4 text-lg text-ink-muted">
          A marketplace that claims to be transparent has to be transparent about its own rules.
          This page is the whole of them.
        </p>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">Who sees a program’s terms</h2>
          <p className="mt-3 text-ink-muted">
            A public program’s commission type, rate, cookie window, payout schedule and content
            requirements are visible to anyone — logged out, logged in, or a search crawler.
          </p>
          <p className="mt-3 text-ink-muted">
            A brand can also run a private program. It stays out of the directory, the brand’s page
            and search, and its terms go only to the creators the brand invites or accepts — always
            before they post anything, and frozen onto their partnership like any other terms.
          </p>
          <p className="mt-3 text-ink-muted">
            One field on a program is not public: the secret used to sign server-to-server
            conversion callbacks. Publishing it would let anyone fabricate conversions against that
            brand.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">
            Verified, self-reported and tracked
          </h2>
          <p className="mt-3 text-ink-muted">
            When a creator connects TikTok, Instagram, YouTube or a Facebook Page, we read that
            account’s followers, average views and engagement from the platform itself, with their
            permission, and refresh them daily. Those are marked verified, with the date they were
            read. Creators need one connected account to apply to a program.
          </p>
          <p className="mt-3 text-ink-muted">
            Anything else a creator enters — a blog’s readers, a newsletter’s subscribers,
            demographics — is labelled self-reported next to the number itself, not in a footnote.
            Nobody can mark a typed-in number verified, and a verified one cannot be edited by hand.
          </p>
          <p className="mt-3 text-ink-muted">
            Numbers Affily measured — clicks, conversions, commissions, completed partnerships — are
            shown separately and marked as tracked.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">The transparency score</h2>
          <p className="mt-3 text-ink-muted">
            A score out of 100 measuring how much checkable information a profile carries. It is a
            flat weighted sum — no model, no smoothing, no manual adjustment. It measures
            disclosure, not quality or performance, and nothing about it is for sale.
          </p>

          <div className="mt-6 overflow-x-auto rounded-lg border border-rule bg-paper-raised">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Criterion</th>
                  <th className="num">Points</th>
                </tr>
              </thead>
              <tbody>
                {TRANSPARENCY_CRITERIA.map((c) => (
                  <tr key={c.key}>
                    <td>
                      {c.label}
                      <span className="block text-xs text-ink-faint">{c.hint}</span>
                    </td>
                    <td className="num font-medium">{c.weight}</td>
                  </tr>
                ))}
                <tr>
                  <td className="font-semibold">Total</td>
                  <td className="num font-semibold">100</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="mt-8 text-lg font-semibold">Tiers</h3>
          <ul className="mt-2 space-y-1 text-ink-muted">
            {TRANSPARENCY_TIERS.map((t) => (
              <li key={t.key}>
                <span className="font-medium text-ink">{t.label}</span> — {t.min} and above
              </li>
            ))}
          </ul>

          <p className="mt-6 text-ink-muted">
            The creator directory is sorted by this score by default. That is the only ranking
            signal that is on by default, and the single way to move up it is to publish more that a
            brand can independently check.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">Affily Boost, and what it can’t do</h2>
          <p className="mt-3 text-ink-muted">
            Boost buys a labelled featured slot above the directory results. It is returned by the
            API in a separate list and is never merged into the organic results — a boosted program
            cannot move a single organic row, and there is a test asserting the organic ordering is
            byte-identical with a boost applied.
          </p>
          <Link to="/boost" className="mt-3 inline-block text-sm font-medium text-accent hover:underline">
            See what Boost costs →
          </Link>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-semibold tracking-tight">How the numbers are counted</h2>
          <dl className="mt-4 space-y-4">
            <div>
              <dt className="font-medium">Unique clicks</dt>
              <dd className="text-ink-muted">
                Repeat visits from the same address and browser within{' '}
                {CLICK_DEDUP_WINDOW_MS / 3_600_000} hours count once. Duplicate clicks are still
                recorded and shown as total clicks, so you can see both.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Attribution</dt>
              <dd className="text-ink-muted">
                Last click, inside the cookie window the partnership was accepted under. A
                conversion we can’t attribute is recorded, counted and shown to the brand with the
                reason — never silently dropped, because dropping it would flatter every conversion
                rate on the platform.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Frozen terms</dt>
              <dd className="text-ink-muted">
                A partnership copies the program’s terms at acceptance. Editing the program later
                changes nothing about commissions already being earned.
              </dd>
            </div>
            <div>
              <dt className="font-medium">Empty denominators</dt>
              <dd className="text-ink-muted">
                A partnership with no clicks has an unknown EPC, not an EPC of zero. Those render as
                a dash. We would rather show you nothing than a number we can’t stand behind.
              </dd>
            </div>
            <div>
              <dt className="font-medium">The fee</dt>
              <dd className="text-ink-muted">
                A share of each commission, split between the brand and the creator, taken once when
                it accrues and shown on the commission line. Each side sees its rate in its account.
                Never applied twice, never applied to a commission we didn’t track.
              </dd>
            </div>
          </dl>
        </section>
      </article>
    </>
  );
}
